import { useEffect, useState, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/authContext";
import { useNavigate } from "react-router-dom";
import { Button, Badge, Loader, Select, Menu, TextInput } from "@mantine/core";
import { IconChevronDown, IconSearch } from "@tabler/icons-react";

import AppHdWithReturnLeft from "../../composents/Headers/AppHeaders/AppHdWithReturnLeft/AppHdWithReturnLeft";

const API = "https://boldee-97d8d36af3bd.herokuapp.com";

const contratLabels = {
  Vente: { buyer: "Acheteur", seller: "Vendeur" },
  Location: { buyer: "Locataire", seller: "Bailleur" },
  Don: { buyer: "Donataire", seller: "Donateur" },
};

function fundsStatusOf(tx) {
  const sellerWord = tx.offer?.contrat === "Location" ? "bailleur" : "vendeur";
  const buyerWord = (contratLabels[tx.offer?.contrat] || contratLabels.Vente).buyer.toLowerCase();

  switch (tx.status) {
    case "pending_payment": return { label: "En attente de paiement", color: "gray" };
    case "funds_secured":
    case "validated": return { label: "Fonds sécurisés", color: "blue" };
    case "to_released": return { label: `À verser au ${sellerWord}`, color: "red" };
    case "released": return { label: `${sellerWord.charAt(0).toUpperCase() + sellerWord.slice(1)} payé`, color: "green" };
    case "disputed": return { label: "Litige", color: "yellow" };
    case "refunded": return { label: `${buyerWord.charAt(0).toUpperCase() + buyerWord.slice(1)} remboursé`, color: "teal" };
    case "cancelled_buyer": return { label: `À rembourser au ${buyerWord}`, color: "orange" };
    case "cancelled_seller": return { label: `À rembourser au ${buyerWord}`, color: "orange" };
    default: return { label: tx.status, color: "gray" };
  }
}

function stateLabelOf(derivedState, contrat) {
  const labels = contratLabels[contrat] || contratLabels.Vente;
  const article = (word) => /^[aeiouéèAEIOUÉÈ]/.test(word) ? "l'" : "le ";
  const sellerWord = `${article(labels.seller)}${labels.seller.toLowerCase()}`;
  const buyerWord = `${article(labels.buyer)}${labels.buyer.toLowerCase()}`;

  const stateLabels = {
    refused_by_seller: { label: `Refusé par ${sellerWord}`, color: "red" },
    cancelled_by_buyer: { label: `Annulé par ${buyerWord}`, color: "orange" },
    cancelled_by_seller_penalty: { label: `Annulé par ${sellerWord}`, color: "orange" },
    no_response: { label: "Achat sans réponse", color: "gray" },
    accepted_by_seller: { label: `Accepté par ${sellerWord}`, color: "green" },
    auto_accepted: { label: "Accepté automatiquement (72h)", color: "teal" },
    pending: { label: "En attente", color: "gray" },
  };

  return stateLabels[derivedState] || stateLabels.pending;
}

const AdminTransactions = () => {
  const { getToken, getUserType, handleLogoutAdmin } = useContext(AuthContext);
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [comments, setComments] = useState({}); // { [transactionId]: string } — édition locale avant sauvegarde
  const [savingComment, setSavingComment] = useState(null);

  // Filtres par colonne, appliqués côté client sur la liste déjà récupérée (le filtre par
  // statut ci-dessus reste géré côté serveur via l'URL, ceux-ci sont juste du raffinage local).
  const [offerTitleFilter, setOfferTitleFilter] = useState("");
  const [referenceFilter, setReferenceFilter] = useState("");
  const [contratFilter, setContratFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [reseauFilter, setReseauFilter] = useState("");

  useEffect(() => {
    const token = getToken();
    if (!token) { navigate("/adm/login"); return; }
    const userType = getUserType();
    if (userType !== "admin") { handleLogoutAdmin(); navigate("/adm/login"); return; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const url = filter
        ? `${API}/adm/bu/transactions?status=${filter}`
        : `${API}/adm/bu/transactions`;

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      let data = await res.json();
      if (!Array.isArray(data)) data = [];
      setTransactions(data);
      setComments(Object.fromEntries(data.map(tx => [tx._id, tx.adminComment || ""])));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveComment = async (id) => {
    const original = transactions.find(tx => tx._id === id)?.adminComment || "";
    const value = comments[id] ?? "";
    if (value === original) return;

    setSavingComment(id);
    try {
      const token = getToken();
      const res = await fetch(`${API}/adm/bu/transactions/${id}/comment`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ comment: value })
      });
      if (!res.ok) { alert("Erreur lors de l'enregistrement du commentaire."); return; }
      setTransactions(prev => prev.map(tx => tx._id === id ? { ...tx, adminComment: value } : tx));
    } catch (err) {
      console.error(err);
    } finally {
      setSavingComment(null);
    }
  };

  const doAction = async (id, endpoint) => {
    setActionLoading(id + endpoint);
    try {
      const token = getToken();
      const res = await fetch(`${API}/adm/bu/transactions/${id}/${endpoint}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { alert("Erreur lors de l'action."); return; }
      await fetchTransactions();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "-";
  const fmtAmount = (n) => n != null ? `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : "-";
  const fmtAmountCents = (n) => n != null ? `${(Number(n) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : "-";

  const normalize = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  const filteredTransactions = useMemo(() => {
    return transactions.filter(tx => {
      if (offerTitleFilter && !normalize(tx.offer?.title).includes(normalize(offerTitleFilter))) return false;
      if (referenceFilter && !normalize(tx.reference).includes(normalize(referenceFilter))) return false;
      if (contratFilter && tx.offer?.contrat !== contratFilter) return false;
      if (stateFilter && tx.derivedState !== stateFilter) return false;
      if (reseauFilter && !normalize(tx.providerId?.company?.reseau).includes(normalize(reseauFilter))) return false;
      return true;
    });
  }, [transactions, offerTitleFilter, referenceFilter, contratFilter, stateFilter, reseauFilter]);

  const HEADER_ROW_HEIGHT = 32; // th padding (6px*2) + line-height à fontSize 11, fixé explicitement pour aligner la ligne de filtres sticky juste en dessous
  const th = { padding: "6px 6px", whiteSpace: "normal", wordBreak: "break-word", fontWeight: 500, height: HEADER_ROW_HEIGHT, boxSizing: "border-box" };
  const td = { padding: "6px 6px", whiteSpace: "normal", wordBreak: "break-word" };
  const thSticky = { ...th, position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 2, borderBottom: "2px solid #ccc" };
  const filterRowCellStyle = { padding: "4px 6px", position: "sticky", top: HEADER_ROW_HEIGHT, backgroundColor: "#fff", zIndex: 2, borderBottom: "2px solid #ccc" };
  // Les Badge Mantine tronquent leur texte sur une seule ligne par défaut (overflow hidden +
  // hauteur fixe) : dans des colonnes étroites (table-layout: fixed), ça fait chevaucher le
  // texte au lieu de passer à la ligne. On force un retour à la ligne et une hauteur libre.
  const wrapBadgeStyles = { root: { height: "auto", whiteSpace: "normal", textAlign: "center", padding: "4px 8px" }, inner: { whiteSpace: "normal", overflow: "visible" } };

  if (loading) return (
    <div className="loaderWrapper vertical">
      <Loader color="green" size="sm" type="dots" />
      <div className="mrgT5">Chargement...</div>
    </div>
  );

  return (
    <div className="width95pCenter">
      <AppHdWithReturnLeft>Transactions</AppHdWithReturnLeft>

      <div className="horizontal spaceBtw iCenter mrgT10 mrgB10">
        <p className="noMrgTB">
          {filteredTransactions.length} transaction{filteredTransactions.length > 1 ? "s" : ""}
          {filteredTransactions.length !== transactions.length && ` (sur ${transactions.length})`}
        </p>
        <Select
          placeholder="Filtrer par statut"
          value={filter}
          onChange={(v) => setFilter(v || "")}
          clearable
          data={[
            { value: "funds_secured", label: "Fonds sécurisés" },
            { value: "validated", label: "Validées" },
            { value: "to_released", label: "À verser au vendeur" },
            { value: "released", label: "Versées" },
            { value: "cancelled_seller", label: "Annulées (vendeur)" },
            { value: "cancelled_buyer", label: "Annulées (acheteur)" },
            { value: "disputed", label: "Litiges" },
            { value: "refunded", label: "Remboursées" },
          ]}
          style={{ width: 220 }}
        />
      </div>

      <div style={{ overflow: "auto", maxHeight: "75vh", border: "1px solid #eee" }}>
        <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 11, fontWeight: 400 }}>
          <thead>
            <tr style={{ textAlign: "left" }}>
              <th style={thSticky}>Date</th>
              <th style={thSticky}>N° transaction</th>
              <th style={thSticky}>Offre</th>
              <th style={thSticky}>Contrat</th>
              <th style={thSticky}>Acheteur / Locataire / Donataire</th>
              <th style={thSticky}>Vendeur / Bailleur / Donateur</th>
              <th style={thSticky}>Prix app</th>
              <th style={thSticky}>Caution</th>
              <th style={thSticky}>Période</th>
              <th style={thSticky}>Frais de service acheteur HT</th>
              <th style={thSticky}>TVA (frais acheteur)</th>
              <th style={thSticky}>Encaissé Stripe</th>
              <th style={thSticky}>Frais de service vendeur HT</th>
              <th style={thSticky}>TVA (frais vendeur)</th>
              <th style={thSticky}>À verser au vendeur</th>
              <th style={thSticky}>Statut des fonds</th>
              <th style={thSticky}>État</th>
              <th style={thSticky}>Raison annulation/refus</th>
              <th style={thSticky}>Réseau</th>
              <th style={thSticky}>Actions</th>
              <th style={thSticky}>Commentaire</th>
            </tr>
            <tr>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}>
                <TextInput
                  size="xs"
                  placeholder="N° transaction"
                  value={referenceFilter}
                  onChange={(e) => setReferenceFilter(e.currentTarget.value)}
                  icon={<IconSearch size={12} />}
                />
              </td>
              <td style={filterRowCellStyle}>
                <TextInput
                  size="xs"
                  placeholder="Titre de l'offre"
                  value={offerTitleFilter}
                  onChange={(e) => setOfferTitleFilter(e.currentTarget.value)}
                  icon={<IconSearch size={12} />}
                />
              </td>
              <td style={filterRowCellStyle}>
                <Select
                  size="xs"
                  placeholder="Tous"
                  value={contratFilter}
                  onChange={(v) => setContratFilter(v || "")}
                  clearable
                  data={["Vente", "Location", "Don"]}
                  style={{ width: "100%" }}
                />
              </td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}>
                <Select
                  size="xs"
                  placeholder="Tous"
                  value={stateFilter}
                  onChange={(v) => setStateFilter(v || "")}
                  clearable
                  data={[
                    { value: "pending", label: "En attente" },
                    { value: "accepted_by_seller", label: "Accepté" },
                    { value: "auto_accepted", label: "Accepté auto (72h)" },
                    { value: "refused_by_seller", label: "Refusé" },
                    { value: "cancelled_by_buyer", label: "Annulé (acheteur)" },
                    { value: "cancelled_by_seller_penalty", label: "Annulé (vendeur)" },
                    { value: "no_response", label: "Sans réponse" },
                  ]}
                  style={{ width: "100%" }}
                />
              </td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}>
                <TextInput
                  size="xs"
                  placeholder="Réseau"
                  value={reseauFilter}
                  onChange={(e) => setReseauFilter(e.currentTarget.value)}
                  icon={<IconSearch size={12} />}
                />
              </td>
              <td style={filterRowCellStyle}></td>
              <td style={filterRowCellStyle}></td>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map(tx => {
              const labels = contratLabels[tx.offer?.contrat] || contratLabels.Vente;
              const fundsStatus = fundsStatusOf(tx);
              const state = stateLabelOf(tx.derivedState, tx.offer?.contrat);
              const netToSeller = (tx.subtotal || 0) - (tx.sellerCommission || 0) - (tx.sellerCommissionVat?.amount || 0);
              const hasCaution = tx.cautionAmount > 0;
              const isLocation = tx.offer?.contrat === "Location";

              const canPaySeller = tx.status === "to_released";
              const canRefundBuyer = ["cancelled_seller", "cancelled_buyer"].includes(tx.status);

              const hasAnyAction = canPaySeller || canRefundBuyer;

              // "Fait" : plus aucune action possible ET au moins une action a réellement été
              // exécutée depuis l'admin (releasedAt/refundedAt ne sont posés que par ces
              // actions, jamais ailleurs) — distingue de "jamais eu d'action à faire".
              const allActionsDone =
                !hasAnyAction &&
                (
                  (tx.status === "released" && !!tx.releasedAt) ||
                  (tx.status === "refunded" && !!tx.refundedAt)
                );

              return (
                <tr key={tx._id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={td}>{fmtDate(tx.createdAt)}</td>
                  <td style={td}>{tx.reference}</td>
                  <td style={td}>{tx.offer?.title || "-"}</td>
                  <td style={td}>
                    <Badge size="sm" color={tx.offer?.contrat === "Location" ? "indigo" : tx.offer?.contrat === "Don" ? "teal" : "dark"} variant="light" styles={wrapBadgeStyles}>
                      {tx.offer?.contrat || "-"}
                    </Badge>
                  </td>
                  <td style={td}>
                    {tx.buyerId?.company?.name || `${tx.buyerId?.firstname || ""} ${tx.buyerId?.lastname || ""}`.trim() || "-"}
                    <div style={{ fontSize: 9, color: "#999" }}>{labels.buyer}</div>
                  </td>
                  <td style={td}>
                    {tx.providerId?.company?.name || `${tx.providerId?.firstname || ""} ${tx.providerId?.lastname || ""}`.trim() || "-"}
                    <div style={{ fontSize: 9, color: "#999" }}>{labels.seller}</div>
                  </td>
                  <td style={td}>{fmtAmount(tx.subtotal)}</td>
                  <td style={td}>{hasCaution ? fmtAmount(tx.cautionAmount) : "-"}</td>
                  <td style={td}>{isLocation && tx.startDate ? `${fmtDate(tx.startDate)} → ${fmtDate(tx.endDate)}` : "-"}</td>
                  <td style={td}>{fmtAmount(tx.commission)}</td>
                  <td style={td}>
                    {tx.commissionVat
                      ? `${fmtAmount(tx.commissionVat.amount)} (${Math.round(tx.commissionVat.rate * 100)}%)`
                      : "-"}
                  </td>
                  <td style={td}>{fmtAmountCents(tx.amount)}</td>
                  <td style={td}>{fmtAmount(tx.sellerCommission)}</td>
                  <td style={td}>
                    {tx.sellerCommissionVat
                      ? `${fmtAmount(tx.sellerCommissionVat.amount)} (${Math.round(tx.sellerCommissionVat.rate * 100)}%)`
                      : "-"}
                  </td>
                  <td style={td}>{fmtAmount(netToSeller)}</td>
                  <td style={td}>
                    <Badge size="sm" color={fundsStatus.color} styles={wrapBadgeStyles}>{fundsStatus.label}</Badge>
                  </td>
                  <td style={td}>
                    <Badge size="sm" color={state.color} variant="light" styles={wrapBadgeStyles}>{state.label}</Badge>
                  </td>
                  <td style={td}>{tx.cancellationReason || "-"}</td>
                  <td style={td}>{tx.providerId?.company?.reseau || "-"}</td>
                  <td style={td}>
                    {hasAnyAction ? (
                      <Menu shadow="md" width={230} position="bottom-end">
                        <Menu.Target>
                          <Button size="xs" radius="xl" variant="outline" color="dark" rightSection={<IconChevronDown size={14} />}>
                            Actions
                          </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {canPaySeller && (
                            <Menu.Item
                              disabled={actionLoading === tx._id + "release"}
                              onClick={() => doAction(tx._id, "release")}
                            >
                              {isLocation ? "Payer le bailleur" : "Payer le vendeur"}
                            </Menu.Item>
                          )}
                          {canRefundBuyer && (
                            <Menu.Item
                              disabled={actionLoading === tx._id + "refund-confirmed"}
                              onClick={() => doAction(tx._id, "refund-confirmed")}
                            >
                              Rembourser {labels.buyer.toLowerCase()}
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    ) : allActionsDone ? (
                      <Badge size="sm" color="green" variant="light">Fait</Badge>
                    ) : (
                      <span style={{ color: "#bbb" }}>—</span>
                    )}
                  </td>
                  <td style={td}>
                    <TextInput
                      size="xs"
                      placeholder="Ajouter un commentaire"
                      value={comments[tx._id] ?? ""}
                      onChange={(e) => {
                        const newValue = e.currentTarget?.value;
                        if (newValue === undefined) return;
                        setComments(prev => ({ ...prev, [tx._id]: newValue }));
                      }}
                      onBlur={() => saveComment(tx._id)}
                      disabled={savingComment === tx._id}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminTransactions;
